import React from "react";
import classNames from "classnames";
import Checkbox from "@mui/material/Checkbox";

import styles from "./ForecastDataGrid.module.scss";
import { TableRow, temperatureHour } from "./ForecastDataGrid";

interface ConsumptionForecastFixedTableProps {
  /** Row labels, e.g. "00:00 - 00:15". */
  MTUs: string[];
  tableMatrix: TableRow[];
  forecastTemperatures: Record<string, number>;
  isEditable: boolean;
  handleSetCorrection: (value: number, rowIndex: number) => void;
}

/**
 * The reference columns of the consumption grid — period, the average of the selected days, the
 * correction, the forecast the two add up to, and the forecast day's temperature. Rendered as its own
 * table pinned to the left of the scrolling day columns, the same way the production forecast grid does
 * it, so correcting a value no longer means scrolling right and back again to read the result.
 */
const ConsumptionForecastFixedTable = ({
  MTUs,
  tableMatrix,
  forecastTemperatures,
  isEditable,
  handleSetCorrection,
}: ConsumptionForecastFixedTableProps) => {
  const forecastTemperatureAt = (row: TableRow) =>
    forecastTemperatures[`${temperatureHour(row)}:00`];

  /** Mirrors the day column header so both tables' rows stay aligned. */
  const header = (label: string, sublabel?: string) => (
    <div className={styles.dateCell}>
      <p className={styles.date}>{label}</p>
      <p className={styles.day}>{sublabel ?? <>&nbsp;</>}</p>
      <p className={styles.matchedBlocks}>&nbsp;</p>
      {isEditable && (
        <span style={{ visibility: "hidden" }}>
          <Checkbox size="small" sx={{ m: 0, p: 0 }} />
        </span>
      )}
    </div>
  );

  return (
    <div className={classNames(styles.forecastDataGrid, styles.fixedTable)}>
      <table className={classNames(styles.table, styles.isEditing)}>
        <thead>
          <tr>
            <th className={styles.period}>{header("Period")}</th>
            <th className={styles.average}>{header("Average")}</th>
            <th className={styles.correction}>{header("Correction")}</th>
            <th className={styles.forecast}>{header("Forecast")}</th>
            <th className={styles.forecastTemperature}>
              {header("Temp", "°C")}
            </th>
          </tr>
        </thead>
        <tbody>
          {tableMatrix.map((row: TableRow, rowIndex: number) => (
            <tr key={rowIndex}>
              <td className={styles.period}>{MTUs[rowIndex]}</td>
              <td className={styles.average}>{row.average}</td>
              <td className={styles.correction}>
                <input
                  type="number"
                  className={styles.correctionInput}
                  value={row.correction}
                  onChange={(e) =>
                    handleSetCorrection(parseFloat(e.target.value), rowIndex)
                  }
                  step="0.1"
                  // This table is always in its editing style so the pinned columns stay legible;
                  // the input is what has to respect a forecast that is no longer editable.
                  disabled={!isEditable}
                ></input>
              </td>
              <td className={styles.forecast}>{row.forecast}</td>
              <td className={styles.forecastTemperature}>
                {forecastTemperatureAt(row) ?? "–"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ConsumptionForecastFixedTable;
